import "./App.css";
import { FC, useEffect, useState } from "react";
import {
  AppDataInterface,
  CountryInterface,
  UserInterface,
} from "./interfaces";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import GlobalLoader from "./components/GlobalLoader";
import { Routes, Route } from "react-router-dom";
import { Subscribe } from "@react-rxjs/core";
import AppRoutes from "./utils/AppRoutes";
import NotFound from "./modules/NotFound";
import Alert from "./components/Alert";
import {
  alertService,
  CreateOrUpdateSession,
  getBaseAppData,
  getBusinessUnits,
  getCountries,
  getTaxIdentificationTypes,
  loaderService,
  userLogin,
} from "./services";
import {
  setInmutableAppData,
  setInmutableBusinessServices,
  setInmutableBusinessUnits,
  setInmutableCountries,
  setInmutableCurrencyList,
  setInmutableExchangeRate,
  setInmutablePaymentModes,
  setInmutablePieceCategories,
  setInmutableRangeDeclareList,
  setInmutableRateItems,
  setInmutableVolumeMin,
  setInmutableHighDeclaredValue,
  setInmutableDensityMax,
  setInmutableTaxIdentificationTypes,
  setUser,
} from "./store/slices";
import classNames from "classnames";
import LoadingIcon from "./components/LoadingIcon";
import Modal from "./components/Modal";
import { PrimaryButton } from "./components/Buttons";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { hasNewerDeployment } from "./utils/appVersion";

interface AppProps {}
const App: FC<AppProps> = () => {
  const dispatch = useAppDispatch();
  const countries = useAppSelector((state) => state.inmutable.countries);
  const venCountry = countries.find(
    (c: CountryInterface) => c.countryCodeIso === "VEN"
  );

  const [logged, setLogged] = useState(false);
  const [openReloadModal, setOpenReloadModal] = useState(false);

  // Cada 5 minutos y al volver a la pestaña: ¿hay una versión nueva desplegada? Compara la del
  // build con /version.json del mismo servidor. En local no hay versión de build: no consulta.
  useEffect(() => {
    const buildVersion = process.env.REACT_APP_VERSION;
    if (!buildVersion) return;
    const check = async () => {
      if (await hasNewerDeployment(buildVersion)) setOpenReloadModal(true);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    check();
    const intervalId = setInterval(check, 300000);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Fetch countries and set them in the store
  useEffect(() => {
    const findCountries = async () => {
      const countries = await getCountries();
      if (countries) {
        dispatch(setInmutableCountries(countries));
      }
    };

    findCountries();
  }, [dispatch]);

  // Fetch app data and set them in the store
  useEffect(() => {
    const getAppData = async () => {
      const response = await getBaseAppData(venCountry?.id || null);

      if (!response.didError && !!response.model) {
        const appDataDto = response.model;
        // Set App Data
        dispatch(
          setInmutableAppData({
            applicationName: appDataDto.applicationName,
            applicationID: 12, //appDataDto.applicationID,
            applicationDescription: appDataDto.applicationDescription,
            applicationCode: appDataDto.applicationCode,
          } as AppDataInterface)
        );
        dispatch(setInmutableVolumeMin(appDataDto.volumenMin?.[0]));
        dispatch(setInmutableDensityMax(appDataDto.densityMax?.[0]));
        dispatch(
          setInmutableHighDeclaredValue(appDataDto.highDeclaredValue?.[0])
        );
        // Set Exchange Rate
        if (!!appDataDto.exchangeRate) {
          dispatch(setInmutableExchangeRate(appDataDto.exchangeRate));
        }
        // Set Business Services
        if (!!appDataDto.businessServiceList) {
          dispatch(
            setInmutableBusinessServices(appDataDto.businessServiceList)
          );
        }
        // Set Rate Item List
        if (!!appDataDto.rateItemList) {
          dispatch(setInmutableRateItems(appDataDto.rateItemList));
        }
        // Set Piece Categories
        if (!!appDataDto.shippingCategoryList) {
          const categories = appDataDto.shippingCategoryList.map((c) => ({
            id: c.categoryID,
            name: c.categoryName,
            code: c.categoryCode,
            descriptionTags: c.descriptionTags,
          }));
          dispatch(setInmutablePieceCategories(categories));
        }
        // Set Currency List
        if (!!appDataDto.currencyList) {
          dispatch(setInmutableCurrencyList(appDataDto.currencyList));
        }
        // Set Payment Mode List
        if (!!appDataDto.paymentModeList) {
          dispatch(setInmutablePaymentModes(appDataDto.paymentModeList));
        }
        // Set Range Declare List
        if (!!appDataDto.rangeDeclareList) {
          dispatch(setInmutableRangeDeclareList(appDataDto.rangeDeclareList));
        }
      } else {
        alertService.error(
          "Error al cargar los datos de la aplicación",
          response.errorMessage,
          { autoClose: false }
        );
      }
    };
    getAppData();
  }, [dispatch, venCountry?.id]);

  // Fetch stores
  useEffect(() => {
    const getAllBusinessUnits = async () => {
      const businessUnits = await getBusinessUnits();

      if (!!businessUnits) {
        dispatch(setInmutableBusinessUnits(businessUnits));
      }
    };

    getAllBusinessUnits();
  }, [dispatch]);

  useEffect(() => {
    const findTaxIdentificationTypes = async () => {
      const taxIdTypes = await getTaxIdentificationTypes();
      if (!!taxIdTypes) {
        dispatch(setInmutableTaxIdentificationTypes(taxIdTypes));
      }
    };

    findTaxIdentificationTypes();
  }, [dispatch]);

  useEffect(() => {
    const Login = async () => {
      loaderService.start();

      while (true) {
        const userLogged = await userLogin(
          process.env.REACT_APP_SHIPMENT_APP_USER!,
          process.env.REACT_APP_SHIPMENT_APP_PASS!,
          "120"
        );
        const user = userLogged as UserInterface;
        const token = user?.token;
        dispatch(setUser(user));
        localStorage.setItem("Token", token);

        const session = await CreateOrUpdateSession(user.userID, "120", "NN");
        if (session.didError) {
          console.log(session.errorMessage);
        } else {
          break;
        }
      }

      setLogged(true);
      loaderService.stop();
    };

    Login();
  }, [dispatch]);

  return (
    <Subscribe>
      <Modal openModal={openReloadModal} setOpenModal={() => window.location.reload()}>
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <ExclamationTriangleIcon className="text-orange-500 h-20 w-20 mx-auto opacity-90" />
          <h2 className="text-xl font-semibold text-gray-700 text-center">Nueva versión de Guías Tealca</h2>
          <p className="text-gray-600 text-center">
            Hay una actualización disponible. Recarga la página para continuar.
          </p>
          <PrimaryButton onClick={() => window.location.reload()} className="px-4 py-2">
            Recargar
          </PrimaryButton>
        </div>
      </Modal>
      {logged && (
        <Routes>
          {AppRoutes.map((route, index) => {
            const { element, ...rest } = route;
            return <Route key={index} {...rest} element={element} />;
          })}
          <Route path="*" element={<NotFound />} />
          <Route
            path="/*/shipment-not-found"
            element={<NotFound shipmentNotFound />}
          />
          <Route
            path="/*/document-not-found"
            element={<NotFound documentNotFound />}
          />
        </Routes>
      )}

      {!logged && (
        <div className="flex items-center justify-center">
          <div
            className={classNames(
              "flex flex-col items-center justify-center flex-1 p-8 rounded-2xl shadow-lg bg-white mt-20 min-w-20 min-h-20 max-w-64 max-h-64"
            )}
          >
            <LoadingIcon size="5rem" />
            <p className="mt-2 text-xl text-center font-light text-gray-900">
              Cargando página
            </p>
          </div>
        </div>
      )}

      <Alert />
      <GlobalLoader />
    </Subscribe>
  );
};

export default App;
